import { useState } from 'react';
import SimpleLayout from '../../../layouts/SimpleLayout';
import { Spinner } from 'react-bootstrap';
import logo from '../../../assets/img/logo.png';
import { formatAxiosError } from '../../../utils/formatAxiosError';
import { Helmet } from 'react-helmet-async';
import Swal from 'sweetalert2';
import apiClient from '../../../services/api/apiClient';
import AlertComponent from '../../../components/utility/AlertComponent';
import IAlertType from '../../../utils/types/props/IAlertType';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [resetLinkSend, setResetLinkSended] = useState<boolean>(false);

    // ---

    const { mutate, isPending: isOperationProgress } = useMutation({
        mutationFn: () =>
            apiClient.post('/api/auth/forgot-password', {
                email: email,
            }),
        onSuccess: (response) => {
            if (response.data.success === true) {
                Swal.fire({
                    text: 'Opération effectuée avec succès.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    setResetLinkSended(true);
                });
            } else {
                setSystemAlerts((prevAlerts) => [
                    ...prevAlerts,
                    {
                        type: 'danger',
                        message: "Erreur lors l'enregistrement de l'opération. " + response.data.message,
                    },
                ]);
            }
        },
        onError: (err) => {
            addAlert('danger', formatAxiosError(err)?.message ?? "Erreur lors de la soumission de l'adresse e-mail.");
        },
    });

    // ---

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);
    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };
    const addAlert = (type: IAlertType, message: string) => {
        setSystemAlerts((prevAlerts) => [...prevAlerts, { type, message }]);
    };

    const submitForm = () => {
        if (!email) {
            toast("Veuillez entrer votre adresse e-mail.");
            return;
        }

        mutate();
    };

    return (
        <>
            <Helmet>
                <title>{resetLinkSend ? 'Lien de réinitialisation envoyé' : 'Mot de passe oublié'} :: Portail PETROSIL</title>
            </Helmet>

            <div className="page page-center">
                <div className="container container-tight py-4">
                    <div className="text-center mb-4">
                        <a href="/" className="navbar-brand navbar-brand-autodark">
                            <img src={logo} alt="Logo" width="150" height="auto" />
                        </a>
                    </div>

                    {!resetLinkSend ? (
                        <>
                            <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />

                            <div className="card card-md">
                                <div className="card-body">
                                    <h2 className="card-title text-center mb-4">Mot de passe oublié</h2>
                                    <p className="text-secondary mb-4">Entrez votre adresse e-mail, nous vous enverrons un lien de réinitialisation.</p>

                                    <form autoComplete="off">
                                        <div className="mb-3">
                                            <label className="form-label">Adresse e-mail</label>
                                            <input type="email" className="form-control" placeholder="Entrez votre e-mail" value={email} onChange={(e) => setEmail(e.target.value)} />
                                        </div>
                                        <div className="form-footer">
                                            <button
                                                className="btn btn-primary btn-4 w-100"
                                                disabled={isOperationProgress}
                                                type="button"
                                                onClick={() => {
                                                    submitForm();
                                                }}
                                            >
                                                {isOperationProgress ? (
                                                    <>
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                        &nbsp; Soumission
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-2">
                                                            <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z"></path>
                                                            <path d="M3 7l9 6l9 -6"></path>
                                                        </svg>
                                                        Envoyez-moi le lien
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            {!isOperationProgress && (
                                <div className="text-center text-secondary mt-3">
                                    Interface de <a href="/login">connexion</a>.
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="card card-md">
                                <div className="card-body text-center">
                                    <h2 className="card-title mb-4">Lien envoyé !</h2>

                                    <p className="text-secondary mb-4">Si votre adresse e-mail est correcte, vous recevrez sous peu un lien de réinitialisation de mot de passe.</p>
                                    <p className="text-secondary mb-4">Si vous pensez que votre adresse e-mail est correcte mais que vous ne recevez toujours pas le lien, veuillez contacter le support pour obtenir de l’aide.</p>

                                    <div>
                                        <a href="/login" className="btn btn-primary w-100">
                                            Retour à la connexion
                                        </a>
                                    </div>

                                    <div className="hr-text">#</div>

                                    <div className="text-secondary mt-4 fs-5">
                                        Vous ne voyez pas l'e-mail ? Vérifiez votre dossier de spam.
                                        <br />
                                        Mauvaise adresse ?&nbsp;
                                        <a href="/forgot-password" className="text-primary">
                                            Réessayez
                                        </a>
                                        .
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

ForgotPassword.Layout = SimpleLayout;

export default ForgotPassword;
